import invariant from "tiny-invariant"
import type { Address, Chain } from "viem"
import {
  MaxAllowanceExpiration,
  MaxAllowanceTransferAmount,
  MaxOrderedNonce,
  MaxSigDeadline,
  PERMIT_BATCH_TYPES,
  PERMIT_TYPES,
} from "./constants"

export interface PermitDetails {
  token: Address
  amount: bigint
  expiration: number
  nonce: number
}

export interface PermitSingle {
  details: PermitDetails
  spender: Address
  sigDeadline: bigint
}

export interface PermitBatch {
  details: PermitDetails[]
  spender: Address
  sigDeadline: bigint
}

export interface PermitDomain {
  name: "Permit2"
  chainId: Chain["id"]
  verifyingContract: Address
}

/**
 * Checks if the permit is for a single transaction.
 * @param permit - A permit which could be for a single or multiple transactions.
 * @returns True if the permit is for a single transaction.
 */
function isSinglePermit(
  permit: PermitSingle | PermitBatch,
): permit is PermitSingle {
  return !Array.isArray(permit.details)
}

/**
 * Validates the permit details according to predefined constants.
 * @param details - Details of the permit to validate.
 */
function validatePermitDetails(details: PermitDetails): void {
  invariant(MaxOrderedNonce >= details.nonce, "NONCE_OUT_OF_RANGE")
  invariant(MaxAllowanceTransferAmount >= details.amount, "AMOUNT_OUT_OF_RANGE")
  invariant(
    MaxAllowanceExpiration >= details.expiration,
    "EXPIRATION_OUT_OF_RANGE",
  )
}

/**
 * Constructs the permit domain using chain ID and contract address.
 * @param chainId - Blockchain chain ID.
 * @param permit2Address - Contract address for Permit2.
 * @returns The constructed domain of the permit.
 */
function getPermitDomain(
  chainId: Chain["id"],
  permit2Address: Address,
): PermitDomain {
  return { name: "Permit2", chainId, verifyingContract: permit2Address }
}

/**
 * Generates signed type data for a single transaction permit.
 * @param permit - Single permit data.
 * @param chainId - Blockchain chain ID.
 * @param permit2Address - Permit2 contract address.
 * @returns The signed type data excluding account information.
 */
export function getSinglePermit(
  permit: PermitSingle,
  chainId: Chain["id"],
  permit2Address: Address,
) {
  invariant(MaxSigDeadline >= permit.sigDeadline, "SIG_DEADLINE_OUT_OF_RANGE")
  validatePermitDetails(permit.details)

  const domain = getPermitDomain(chainId, permit2Address)
  return {
    domain,
    types: PERMIT_TYPES,
    primaryType: "PermitSingle",
    message: permit,
  }
}

/**
 * Generates signed type data for a batch transaction permit.
 * @param permit - Batch permit data.
 * @param chainId - Blockchain chain ID.
 * @param permit2Address - Permit2 contract address.
 * @returns The signed type data excluding account information.
 */
export function getBatchPermit(
  permit: PermitBatch,
  chainId: Chain["id"],
  permit2Address: Address,
) {
  invariant(MaxSigDeadline >= permit.sigDeadline, "SIG_DEADLINE_OUT_OF_RANGE")
  permit.details.forEach(validatePermitDetails)

  const domain = getPermitDomain(chainId, permit2Address)
  return {
    domain,
    types: PERMIT_BATCH_TYPES,
    primaryType: "PermitBatch",
    message: permit,
  }
}

/**
 * Gets the appropriate permit signed type data based on whether it's a single or batch transaction.
 * @param permit - Either a single or batch permit.
 * @param chainId - Blockchain chain ID.
 * @param permit2Address - Optional contract address.
 * @returns Signed type data for the permit.
 */
export function getPermit(
  permit: PermitSingle | PermitBatch,
  chainId: Chain["id"],
  permit2Address: Address,
) {
  if (isSinglePermit(permit)) {
    return getSinglePermit(permit, chainId, permit2Address)
  }
  return getBatchPermit(permit, chainId, permit2Address)
}
